/**
 * Repertoire playback state machine (MUS-243).
 *
 * This is the authoritative state machine for the repertoire scene's
 * playback lifecycle. All mutations of:
 *
 *   - `mainTimeKeeper` (pause / unpause)
 *   - `timeKeeperAuxiliary` (pause / unpause / resetTime)
 *   - `scheduler2` (unpauseTicks / pauseTicks / stopNativeScheduler /
 *     setLoopBounds / clearLoopBounds / forceStopTickInterval)
 *   - `RepertoireCursor` (enable / disable / pause / unpause / blink /
 *     setStart / setX / moveTo)
 *   - `OSMDEventStream` (seekToTimestamp / resetNoteStatesInRange /
 *     initAccuracyQueues / setErrorRecognitionActive)
 *
 * happen as actions on this machine. Nothing else in the codebase should
 * call those subsystems imperatively.
 *
 * See `roadmap/plans/mus-243-playback-state-machine-plan.md` for the
 * full design, state diagram, side-effect action table, and migration plan.
 *
 * Phase 1 status: pure machine with no real side effects. The side-effect
 * action implementations are noop stubs that log. Phase 2 wires real
 * implementations in the scene's machine setup via `.provide({ actions })`.
 */

import { setup, assign } from 'xstate';
import type { PlaybackContext, PlaybackEvent, PlaybackMachineInput } from './playbackMachine.types';

/** Side-effect action names. Tests use these as the keys when overriding
 *  via `.provide({ actions })`. The scene wires real implementations in
 *  Phase 2 using the same keys. */
export const SIDE_EFFECT_ACTION_NAMES = [
  'armAuxTK',
  'pauseAuxTK',
  'pauseMainTK',
  'unpauseMainTKAt',
  'pauseScheduler2',
  'unpauseScheduler2At',
  'clearScheduler2',
  'setScheduler2LoopBounds',
  'enableCursorBlink',
  'unpauseCursor',
  'pauseCursor',
  'positionCursorAtBracketStart',
  'positionCursorAtLastCompletedMeasure',
  'resetLoopVisuals',
  'disableErrorRecognition',
  'restoreErrorRecognition',
] as const;

export type SideEffectActionName = (typeof SIDE_EFFECT_ACTION_NAMES)[number];

/** Default initial context — used when `input` is omitted (rare; tests
 *  usually pass explicit input). */
const defaultContext: PlaybackContext = {
  tempo: 60,
  isLoopingActive: false,
  bracketStartTimestamp: null,
  bracketEndTimestamp: null,
  hasEverPlayedInThisLoop: false,
  wasInLoopCountInBeforePause: false,
  lastPausedAtTimestamp: undefined,
  pieceCompleted: false,
};

/** Build the side-effect stub map. Each is a no-op that logs.
 *  Tests / Phase 2 override via `.provide({ actions: {...} })`. */
function buildStubSideEffects(): Record<SideEffectActionName, () => void> {
  const map = {} as Record<SideEffectActionName, () => void>;
  for (const name of SIDE_EFFECT_ACTION_NAMES) {
    map[name] = () => {
      // eslint-disable-next-line no-console -- intentional during Phase 1
      console.log(`[LOOP-DBG-243-STUB] action: ${name}`);
    };
  }
  return map;
}

export const playbackMachine = setup({
  types: {
    context: {} as PlaybackContext,
    events: {} as PlaybackEvent,
    input: {} as PlaybackMachineInput | undefined,
  },
  guards: {
    isLooping: ({ context }) => context.isLoopingActive,
    isNotLooping: ({ context }) => !context.isLoopingActive,
    wasInLoopCountInBeforePause: ({ context }) => context.wasInLoopCountInBeforePause,
    hasEverPlayedInThisLoop: ({ context }) => context.hasEverPlayedInThisLoop,
    isTogglingLoopOff: ({ event }) => event.type === 'USER_TOGGLE_LOOP' && !event.active,
  },
  actions: {
    // ---- Side-effect stubs (overridden via .provide in tests / Phase 2) ----
    ...buildStubSideEffects(),

    // ---- Context updaters (pure assigns; never overridden) ----
    setHasEverPlayedTrue: assign({ hasEverPlayedInThisLoop: () => true }),
    setHasEverPlayedFalse: assign({ hasEverPlayedInThisLoop: () => false }),
    setWasInLoopCountInBeforePauseTrue: assign({
      wasInLoopCountInBeforePause: () => true,
    }),
    clearWasInLoopCountInBeforePause: assign({
      wasInLoopCountInBeforePause: () => false,
    }),
    setLoopingActiveFromEvent: assign({
      isLoopingActive: ({ event, context }) =>
        event.type === 'USER_TOGGLE_LOOP' ? event.active : context.isLoopingActive,
    }),
    setBracketFromEvent: assign(({ event, context }) => {
      if (event.type !== 'USER_BRACKET_CHANGE') return {};
      return {
        isLoopingActive: true,
        bracketStartTimestamp: event.bracketStartTimestamp,
        bracketEndTimestamp: event.bracketEndTimestamp,
        hasEverPlayedInThisLoop: false,
        wasInLoopCountInBeforePause: false,
      };
    }),
    setTempoFromEvent: assign({
      tempo: ({ event, context }) => (event.type === 'SET_TEMPO' ? event.tempo : context.tempo),
    }),
    clearBracket: assign({
      isLoopingActive: () => false,
      bracketStartTimestamp: () => null,
      bracketEndTimestamp: () => null,
      hasEverPlayedInThisLoop: () => false,
      wasInLoopCountInBeforePause: () => false,
    }),
  },
}).createMachine({
  id: 'repertoirePlayback',
  initial: 'stopped',
  context: ({ input }) => ({
    ...defaultContext,
    ...(input ?? {}),
  }),
  on: {
    // Tempo updates can happen in any state.
    SET_TEMPO: { actions: 'setTempoFromEvent' },
    // Soft refresh from any state → full stop. `stopped`'s entry pauses
    // main/aux TK + Scheduler2 + cursor and clears Scheduler2; we also clear
    // play-progress context so the next USER_PLAY runs a fresh count-in
    // (otherwise hasEverPlayedInThisLoop would route paused→playing).
    SOFT_REFRESH: {
      target: '.stopped',
      actions: ['setHasEverPlayedFalse', 'clearWasInLoopCountInBeforePause'],
    },
    // Looping toggle off → stopped; on → context update only.
    USER_TOGGLE_LOOP: [
      {
        target: '.stopped',
        guard: 'isTogglingLoopOff',
        actions: ['setLoopingActiveFromEvent', 'clearBracket'],
      },
      { actions: 'setLoopingActiveFromEvent' },
    ],
    // USER_BRACKET_CHANGE behavior is per-state (see each state's `on`):
    //   - stopped / paused: update context + scheduler bounds, stay put.
    //     Enabling looping for the first time, or dragging the bracket while
    //     paused, must NOT prematurely start the count-in.
    //   - loopCountInWait: re-enter to reset visuals at the new bracket.
    //   - playing / normalCountIn: transition to loopCountInWait (the new
    //     bracket invalidates current playback state; user must restart).
  },
  states: {
    // -----------------------------------------------------------------------
    stopped: {
      entry: ['clearScheduler2', 'pauseMainTK', 'pauseAuxTK', 'pauseCursor'],
      on: {
        USER_PLAY: [
          { target: 'loopCountInWait', guard: 'isLooping' },
          { target: 'normalCountIn', guard: 'isNotLooping' },
        ],
        // Enabling looping for the first time sets the initial bracket but
        // must not start the count-in — wait for the user to press play.
        // resetLoopVisuals clears any colored notes from a prior non-loop
        // playthrough so the score is clean under the new bracket (no
        // updateOsmdEventStream tick runs while stopped → no red-note race).
        USER_BRACKET_CHANGE: {
          actions: ['setBracketFromEvent', 'setScheduler2LoopBounds', 'resetLoopVisuals'],
        },
      },
    },

    // -----------------------------------------------------------------------
    normalCountIn: {
      entry: ['armAuxTK', 'enableCursorBlink'],
      exit: ['pauseAuxTK'],
      on: {
        NOTE_PLAYED: {
          target: 'playing',
          actions: ['unpauseMainTKAt', 'setHasEverPlayedTrue'],
        },
        COUNT_IN_FINISHED: {
          target: 'playing',
          actions: ['unpauseMainTKAt', 'setHasEverPlayedTrue'],
        },
        USER_PAUSE: 'paused',
        APP_BACKGROUNDED: 'paused',
        USER_BRACKET_CHANGE: {
          target: 'loopCountInWait',
          actions: ['setBracketFromEvent', 'setScheduler2LoopBounds'],
        },
      },
    },

    // -----------------------------------------------------------------------
    loopCountInWait: {
      // Entering count-in wait ALWAYS means "fresh loop iteration": disable
      // error recognition, then reset the loop visuals — as consecutive
      // synchronous entry actions. Doing both inside one actor.send() makes
      // the legacy red-note race structurally impossible: the async
      // `updateOsmdEventStream` tick cannot interleave between disabling
      // recognition and clearing note states. This is why the scene's
      // legacy setLoopingActive / restartLoop / handleBracketChanged no
      // longer need (and must not duplicate) this work.
      entry: [
        // Re-apply the scheduler's loop bounds from context first.
        // stopped.entry's clearScheduler2 also calls clearLoopBounds (correct
        // when looping is being toggled OFF), so after SOFT_REFRESH the
        // scheduler has no loopStartTimestamp set. Without this re-apply,
        // the subsequent paused-→-playing path unpauses the TK BEFORE
        // playing.entry's unpauseScheduler2At runs, so unpauseTicks hits its
        // "TK already running, no loop bounds" bail-out — no tickIntervalFn,
        // no metronome audio. Re-establishing bounds on every loopCountInWait
        // entry keeps the scheduler in lockstep with the machine context.
        'setScheduler2LoopBounds',
        'pauseMainTK',
        'pauseScheduler2',
        'disableErrorRecognition',
        'resetLoopVisuals',
        'armAuxTK',
        'positionCursorAtBracketStart',
        'enableCursorBlink',
      ],
      exit: ['pauseAuxTK', 'restoreErrorRecognition'],
      on: {
        // USER_PLAY is a no-op in count-in wait — this single transition
        // (or rather, the absence of any state change) is what kills
        // MUS-242's double-metronome case.
        USER_PLAY: {},
        NOTE_PLAYED: {
          target: 'playing',
          actions: ['unpauseMainTKAt', 'setHasEverPlayedTrue'],
        },
        USER_PAUSE: {
          target: 'paused',
          actions: 'setWasInLoopCountInBeforePauseTrue',
        },
        USER_RESET: {
          target: 'loopCountInWait',
          reenter: true,
          // resetLoopVisuals is fired by loopCountInWait's entry (re-enter).
          actions: ['setHasEverPlayedFalse'],
        },
        APP_BACKGROUNDED: {
          target: 'paused',
          actions: 'setWasInLoopCountInBeforePauseTrue',
        },
        USER_BRACKET_CHANGE: {
          target: 'loopCountInWait',
          reenter: true,
          actions: ['setBracketFromEvent', 'setScheduler2LoopBounds'],
        },
      },
    },

    // -----------------------------------------------------------------------
    playing: {
      entry: ['unpauseScheduler2At', 'unpauseCursor'],
      on: {
        USER_PAUSE: 'paused',
        USER_RESET: {
          target: 'loopCountInWait',
          guard: 'isLooping',
          // resetLoopVisuals is fired by loopCountInWait's entry.
          actions: ['setHasEverPlayedFalse'],
        },
        LOOP_BOUNDARY: {
          target: 'loopBoundaryRestart',
          guard: 'isLooping',
        },
        APP_BACKGROUNDED: 'paused',
        USER_BRACKET_CHANGE: {
          target: 'loopCountInWait',
          actions: ['setBracketFromEvent', 'setScheduler2LoopBounds'],
        },
      },
    },

    // -----------------------------------------------------------------------
    paused: {
      entry: [
        'pauseMainTK',
        'pauseAuxTK',
        'pauseScheduler2',
        'pauseCursor',
        'positionCursorAtLastCompletedMeasure',
      ],
      on: {
        USER_PLAY: [
          {
            // MUS-238: if user paused during count-in wait, restore that state.
            target: 'loopCountInWait',
            guard: 'wasInLoopCountInBeforePause',
            actions: 'clearWasInLoopCountInBeforePause',
          },
          {
            // Resume normal playback if we've ever played a note in this loop.
            target: 'playing',
            guard: 'hasEverPlayedInThisLoop',
            actions: 'unpauseMainTKAt',
          },
          {
            // First-play with looping on: go to count-in wait.
            target: 'loopCountInWait',
            guard: 'isLooping',
          },
          {
            // First-play without looping: go to normal count-in.
            target: 'normalCountIn',
          },
        ],
        USER_RESET: [
          {
            // MUS-242 (reset-while-paused): stay paused; reset visuals only.
            target: 'paused',
            guard: 'isLooping',
            reenter: true,
            actions: [
              'resetLoopVisuals',
              'positionCursorAtBracketStart',
              'setHasEverPlayedFalse',
              'clearWasInLoopCountInBeforePause',
            ],
          },
        ],
        APP_FOREGROUNDED: {}, // no-op; user must press play to resume
        // Dragging the bracket while paused updates bounds but keeps the
        // user paused — pressing play resumes from count-in wait (via the
        // wasInLoopCountInBeforePause guard or hasEverPlayed guard).
        USER_BRACKET_CHANGE: {
          actions: [
            'setBracketFromEvent',
            'setScheduler2LoopBounds',
            'positionCursorAtBracketStart',
            'resetLoopVisuals',
          ],
        },
      },
    },

    // -----------------------------------------------------------------------
    loopBoundaryRestart: {
      // Only the context update is unique here; the subsystem work
      // (resetLoopVisuals, positionCursorAtBracketStart, pauseMainTK) is
      // performed by loopCountInWait's entry, which this immediately
      // transitions into. Keeping it here too would double-fire those.
      entry: ['setHasEverPlayedFalse'],
      // Auto-transition to loopCountInWait once entry actions run.
      always: 'loopCountInWait',
    },
  },
});

export type PlaybackMachine = typeof playbackMachine;
